import { Controller, useFormContext } from 'react-hook-form';
// @mui
import TextField, { TextFieldProps, TextFieldPropsColorOverrides } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';

import {
  NumericFormat,
  OnValueChange,
  PatternFormat,
  numericFormatter,
  NumberFormatValues,
} from 'react-number-format';
import { OverridableStringUnion } from '@mui/types';
import { useTranslation } from 'react-i18next';
import { Theme, SxProps } from '@mui/material';
// ----------------------------------------------------------------------

type Props = TextFieldProps & {
  name: string;
  prefix?: string;
  suffix?: string;
  currency?: boolean;
  disabled?: boolean;
  decimalScale?: number;
  variant?: string;
  inputProps?: any;
  fixedDecimalScale?: boolean;
  allowNegative?: boolean;
  thousandsGroupStyle?: 'thousand' | 'lakh' | 'wan' | 'none';
  thousandSeparator?: boolean | string;
  decimalSeparator?: string;
  sx?: SxProps<Theme>;
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    TextFieldPropsColorOverrides
  >;
  endAdornment?: React.ReactNode;
  isAllowed?: (values: NumberFormatValues) => boolean;
  onValueChange?: OnValueChange;
};

export default function RHFNumberField({
  name,
  prefix,
  suffix,
  variant,
  inputProps,
  currency,
  thousandsGroupStyle,
  thousandSeparator,
  decimalSeparator,
  decimalScale = 0,
  fixedDecimalScale,
  allowNegative = true,
  helperText,
  type,
  sx,
  color,
  isAllowed,
  onValueChange,
  disabled,
  endAdornment,
  ...other
}: Props) {
  const { control } = useFormContext();
  const { i18n } = useTranslation();
  const theme = useTheme();

  // Default styling for RHF components
  const defaultSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.neutral',
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: 'divider',
        borderWidth: '1px',
      },
      '&:hover:not(.Mui-focused):not(.Mui-disabled) fieldset': {
        borderColor: 'text.secondary',
      },
      '&.Mui-focused': {
        backgroundColor: 'background.paper',
        '& fieldset': {
          borderWidth: '1px',
        },
      },
      '&.Mui-error': {
        backgroundColor: 'rgba(255, 72, 66, 0.04)',
        '& fieldset': {
          borderColor: '#ff6b6b',
          borderWidth: '1px',
        },
        '&:hover:not(.Mui-focused) fieldset': {
          borderColor: '#ff5252',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#ff4444',
          borderWidth: '2px',
        },
      },
      '&.Mui-disabled': {
        backgroundColor: 'action.hover',
        '& fieldset': {
          borderColor: 'action.disabled',
          borderStyle: 'dashed',
        },
        '& .MuiInputBase-input': {
          color: 'text.disabled',
          WebkitTextFillColor: 'unset',
        },
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      '&::placeholder': {
        color: 'text.disabled',
        opacity: 0.8,
      },
    },
    // Specific padding for endAdornment when there's a suffix
    '& .MuiInputBase-adornedEnd .MuiInputBase-input': {
      paddingRight: suffix && endAdornment ? theme.spacing(6) : theme.spacing(1.75),
    },
    // Style for Material-UI endAdornment
    '& .MuiInputAdornment-positionEnd': {
      color: 'text.secondary',
      fontSize: '0.875rem',
      '& .MuiTypography-root': {
        color: 'text.secondary',
        fontSize: '0.875rem',
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginRight: 0,
      '&.Mui-error': {
        marginTop: theme.spacing(0.75),
      },
    },
  };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const { value } = field;
        if (currency) {
          if (i18n.language === 'es') {
            decimalSeparator = ',';
            thousandSeparator = '.';
          } else {
            decimalSeparator = '.';
            thousandSeparator = ',';
          }
        }
        return (
          <NumericFormat
            label={other.label}
            InputProps={{
              endAdornment: endAdornment ? (
                <InputAdornment position="end" sx={{ marginLeft: suffix ? 1 : 0.5 }}>
                  {endAdornment}
                </InputAdornment>
              ) : undefined,
            }}
            size={other?.size ?? 'small'}
            sx={sx ? { ...defaultSx, ...sx } : defaultSx}
            variant={variant}
            inputProps={inputProps}
            color={color}
            value={value}
            isAllowed={isAllowed}
            disabled={disabled}
            customInput={TextField}
            allowNegative={allowNegative}
            error={!!error}
            helperText={error ? error?.message : helperText}
            FormHelperTextProps={{ sx: { mt: 0, mb: 1 } }}
            decimalScale={decimalScale}
            prefix={prefix}
            suffix={suffix}
            thousandsGroupStyle={thousandsGroupStyle}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            fixedDecimalScale={fixedDecimalScale}
            onValueChange={
              onValueChange ||
              ((values, sourceInfo) => {
                field.onChange(Number(values.floatValue ?? 0));
              })
            }
          />
        );
      }}
    />
  );
}

type PatterProps = TextFieldProps & {
  name: string;
  format: string;
  mask: string;
  prefix?: string;
  suffix?: string;
  currency?: boolean;
  disabled?: boolean;
  decimalScale?: number;
  fixedDecimalScale?: boolean;
  allowNegative?: boolean;
  thousandsGroupStyle?: 'thousand' | 'lakh' | 'wan' | 'none';
  thousandSeparator?: boolean | string;
  decimalSeparator?: string;
  sx?: SxProps<Theme>;
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    TextFieldPropsColorOverrides
  >;
  endAdornment?: React.ReactNode;
  isAllowed?: (values: NumberFormatValues) => boolean;
  onValueChange?: OnValueChange;
};

export function RHFPatternField({
  name,
  prefix,
  suffix,
  format,
  mask,
  thousandsGroupStyle,
  thousandSeparator,
  decimalSeparator,
  decimalScale = 0,
  fixedDecimalScale,
  allowNegative = true,
  helperText,
  type,
  sx,
  color,
  isAllowed,
  onValueChange,
  disabled,
  ...other
}: PatterProps) {
  const { control } = useFormContext();
  const theme = useTheme();

  // Default styling for RHF components
  const defaultSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.neutral',
      // borderRadius: 1.5,
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: 'divider',
        borderWidth: '1px',
      },
      '&:hover:not(.Mui-focused):not(.Mui-disabled) fieldset': {
        borderColor: 'text.secondary',
      },
      '&.Mui-focused': {
        backgroundColor: 'background.paper',
        '& fieldset': {
          // borderColor: '#90a4ae',
          borderWidth: '1px',
        },
      },
      '&.Mui-error': {
        backgroundColor: 'rgba(255, 72, 66, 0.04)',
        '& fieldset': {
          borderColor: '#ff6b6b',
          borderWidth: '1px',
        },
        '&:hover:not(.Mui-focused) fieldset': {
          borderColor: '#ff5252',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#ff4444',
          borderWidth: '2px',
        },
      },
      '&.Mui-disabled': {
        backgroundColor: 'action.hover',
        '& fieldset': {
          borderColor: 'action.disabled',
          borderStyle: 'dashed',
        },
        '& .MuiInputBase-input': {
          color: 'text.disabled',
          WebkitTextFillColor: 'unset',
        },
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      '&::placeholder': {
        color: 'text.disabled',
        opacity: 0.8,
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginRight: 0,
      '&.Mui-error': {
        marginTop: theme.spacing(0.75),
        display: 'inline-block',
        maxWidth: '300px',
        width: 'auto',
      },
    },
  };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const { value } = field;
        return (
          <PatternFormat
            label={other.label}
            value={value}
            format={format}
            mask={mask}
            allowEmptyFormatting
            customInput={TextField}
            size={other?.size ?? 'small'}
            sx={sx ? { ...defaultSx, ...sx } : defaultSx}
            disabled={disabled}
            error={!!error}
            helperText={error ? error?.message : helperText}
            onValueChange={
              onValueChange ||
              ((values, sourceInfo) => {
                field.onChange(values.floatValue);
              })
            }
          />
        );
      }}
    />
  );
}

export function RHFNumberText({
  value,
  decimalScale,
  allowNegative,
  thousandSeparator,
  decimalSeparator,
  fixedDecimalScale,
  thousandsGroupStyle,
  prefix,
  suffix,
}: {
  value: number;
  decimalScale?: number;
  thousandsGroupStyle?: 'thousand' | 'lakh' | 'wan' | 'none';
  thousandSeparator?: boolean | string;
  decimalSeparator?: string;
  fixedDecimalScale?: boolean;
  allowNegative?: boolean;
  prefix?: string;
  suffix?: string;
}) {
  const { i18n } = useTranslation();
  if (i18n.language === 'es') {
    decimalSeparator = ',';
    thousandSeparator = '.';
  } else {
    decimalSeparator = '.';
    thousandSeparator = ',';
  }
  return (
    <NumericFormat
      value={value}
      displayType="text"
      decimalScale={decimalScale}
      thousandsGroupStyle={thousandsGroupStyle}
      thousandSeparator={thousandSeparator}
      decimalSeparator={decimalSeparator}
      fixedDecimalScale={fixedDecimalScale}
      allowNegative={allowNegative}
      prefix={prefix}
      suffix={suffix}
    />
  );
}

export function RHFNumberFormatterText({
  value,
  decimalScale,
  thousandSeparator,
  decimalSeparator,
  fixedDecimalScale,
  thousandsGroupStyle,
  prefix,
  suffix,
}: {
  value: number;
  decimalScale?: number;
  thousandsGroupStyle?: 'thousand' | 'lakh' | 'wan' | 'none';
  thousandSeparator?: boolean | string;
  decimalSeparator?: string;
  fixedDecimalScale?: boolean;
  prefix?: string;
  suffix?: string;
}): string {
  const { i18n } = useTranslation();
  if (i18n.language === 'es') {
    decimalSeparator = ',';
    thousandSeparator = '.';
  } else {
    decimalSeparator = '.';
    thousandSeparator = ',';
  }
  const formatter: string = numericFormatter(value?.toString() ?? '0', {
    decimalScale,
    thousandSeparator,
    decimalSeparator,
    fixedDecimalScale,
    thousandsGroupStyle,
    prefix,
    suffix,
  });
  return formatter;
}
